(function () {
  const seedKey = "bloomr:variant-presentation:seed";
  const choicePrefix = "bloomr:variant-presentation:choice:";

  function readSession(key) {
    try {
      return window.sessionStorage.getItem(key);
    } catch (error) {
      return null;
    }
  }

  function writeSession(key, value) {
    try {
      window.sessionStorage.setItem(key, value);
    } catch (error) {
      // Storage can be unavailable in privacy modes. The deterministic fallback
      // still keeps all cards internally consistent for this page load.
    }
  }

  function getSeed() {
    const storedSeed = readSession(seedKey);
    if (storedSeed) return storedSeed;

    let seed;
    if (window.crypto?.getRandomValues) {
      const values = new Uint32Array(2);
      window.crypto.getRandomValues(values);
      seed = `${values[0]}-${values[1]}`;
    } else {
      seed = `${Date.now()}-${Math.random()}`;
    }

    writeSession(seedKey, seed);
    return seed;
  }

  function hash(value) {
    let result = 2166136261;
    for (let index = 0; index < value.length; index += 1) {
      result ^= value.charCodeAt(index);
      result = Math.imul(result, 16777619);
    }
    return result >>> 0;
  }

  function choose(key, candidates, currentId) {
    const eligible = candidates.filter((candidate) => candidate?.id != null);
    if (!eligible.length) return null;

    const choiceKey = `${choicePrefix}${key}`;
    const storedId = readSession(choiceKey);
    const storedChoice = eligible.find(
      (candidate) => String(candidate.id) === storedId,
    );
    if (storedChoice) return storedChoice;

    const alternatives = eligible.filter(
      (candidate) => String(candidate.id) !== String(currentId),
    );
    const pool = alternatives.length ? alternatives : eligible;
    const selected = pool[hash(`${getSeed()}:${key}`) % pool.length];

    writeSession(choiceKey, String(selected.id));
    return selected;
  }

  function normalizeColor(value) {
    return String(value).normalize("NFKC").toLowerCase()
      .replace(/[^\p{L}\p{N}]+/gu, " ").trim();
  }

  const colorAliases = {
    grey: "gray",
    "أسود": "black", "أزرق": "blue", "كحلي": "navy",
    "بني": "brown", "كريمي": "cream", "ذهبي": "gold",
    "رمادي": "gray", "أخضر": "green", "برتقالي": "orange",
    "وردي": "pink", "أحمر": "red", "أصفر": "yellow", "أبيض": "white",
  };

  function normalizeColorLabel(value) {
    return normalizeColor(value).split(" ").map((word) => colorAliases[word] || word).join(" ");
  }

  function colorWords(value) {
    return normalizeColorLabel(value).split(" ").map((word) => word === "navy" ? "blue" : word);
  }

  const colorFilterNames = [
    "filter.p.m.custom.filter_color", "filter.v.option.color", "filter.v.option.colour",
  ];

  function isColorFilter(name) {
    return colorFilterNames.includes(name);
  }

  function selectedColor(search) {
    let selected = null;
    for (const [key, value] of new URLSearchParams(search)) {
      if (!isColorFilter(key)) continue;
      for (const rawValue of value.split(",")) {
        if (!normalizeColor(rawValue)) continue;
        selected = { name: key, value: rawValue };
      }
    }
    return selected;
  }

  function singleColorParams(search, input) {
    const params = new URLSearchParams(search);
    const selected = isColorFilter(input?.name)
      ? (input.checked ? { name: input.name, value: input.value } : null)
      : selectedColor(params);
    for (const name of colorFilterNames) {
      if (name === selected?.name) params.set(name, selected.value);
      else params.delete(name);
    }
    return params;
  }

  function filterColors(candidates, search) {
    const selected = selectedColor(search);
    if (!selected) return { candidates, key: "" };
    const color = normalizeColor(selected.value);
    const normalizeLabel = selected.name === "filter.p.m.custom.filter_color" ? normalizeColorLabel : normalizeColor;
    const exactColor = normalizeLabel(selected.value);
    const exactMatches = candidates.filter((candidate) => normalizeLabel(candidate.color) === exactColor);
    const matches = exactMatches.length || selected.name !== "filter.p.m.custom.filter_color"
      ? exactMatches
      : candidates.filter((candidate) => colorWords(selected.value).every((word) => colorWords(candidate.color).includes(word)));
    return {
      candidates: matches,
      key: JSON.stringify([selected.name, color]),
    };
  }

  window.BloomrVariantPresentation = { choose, filterColors, singleColorParams, isColorFilter };
})();
